import {useEffect, useMemo, useRef, useState} from 'react';
import {usePage} from '@inertiajs/react';
import {router} from '@inertiajs/core';
import CreateEventDialog from '@/components/dialogs/CreateEventDialog.tsx';
import EditEventDialog from '@/components/dialogs/EditEventDialog.tsx';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {Label} from '@/components/ui/label';
import AppointmentCalendar from '@/components/AppointmentCalendar.tsx';
import AuthorizedLayout from '@/layouts/AuthorizedLayout.tsx';
import {EventPayload, EventUpdatePayload} from '@/lib/types/payloads.ts';
import {CallListPatient, Doctor, Event, Patient} from '@/lib/types/models.ts';
import CallListDialog from '@/components/dialogs/CallListDialog.tsx';
import {Button} from '@/components/ui/button.tsx';
import {ArrowRight, Plus} from 'lucide-react';
import {Calendar} from "@fullcalendar/core";
import MobileAppointmentCalendar from "@/components/MobileAppointmentCalendar.tsx";
import {PageTitle} from "@/components/PageTitle.tsx";
import CreatePatientDialog from "@/components/dialogs/CreatePatientDialog.tsx";
import {PatientFormPayload} from "@/lib/types/forms.ts";
import {toast} from "sonner";

export default function EventsIndex() {
    const {events, doctors, patients, orthodontists, translations: t} = usePage().props as any;

    const [editingEvent, setEditingEvent] = useState<Event | null>(null);

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isCreatePatientDialogOpen, setIsCreatePatientDialogOpen] = useState(false);
    const [selectedAppointmentPatient, setSelectedAppointmentPatient] = useState<Patient | null>(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState<string>('');

    const [selectedPatient, setSelectedPatient] = useState<string>('');

    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [patientSearch, setPatientSearch] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);

    const [isCallListOpen, setIsCallListOpen] = useState(false);
    const [callListPatients, setCallListPatients] = useState<CallListPatient[]>(
        []
    );
    const MOBILE_BREAKPOINT = 768;
    const [isMobile] = useState(
        typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false
    );
    const [nextEventIndex, setNextEventIndex] = useState<number>(0);
    const [lastPatientForNext, setLastPatientForNext] = useState<string | null>(null);
    const calendarRef = useRef<Calendar | null>(null);
    const filteredEvents = useMemo(() => {
        return events.filter((event: Event) => {
            if (selectedDoctor && event.doctor_id !== selectedDoctor) return false;
            return !(selectedPatient && String(event.patient_id) !== String(selectedPatient));
        });
    }, [events, selectedDoctor, selectedPatient]);

    useEffect(() => {
        setNextEventIndex(0);
        setLastPatientForNext(selectedPatient || null);
    }, [selectedPatient]);

    const openCreateDialog = (date?: string) => {
        if (date) setSelectedDate(date);
        setSelectedAppointmentPatient(null);
        setIsCreateDialogOpen(true);
    };

    const openEditDialog = (event: Event) => {
        setEditingEvent(event);
        setIsEditDialogOpen(true);
    };

    const handleCreatePatient = async (data: PatientFormPayload): Promise<Patient> => {
        const csrfToken =
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

        const res = await fetch('/patients', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                ...(csrfToken ? {'X-CSRF-TOKEN': csrfToken} : {}),
            },
            body: JSON.stringify({patient: data}),
            credentials: 'same-origin',
        });

        if (!res.ok) {
            throw new Error('Neuspešno kreiranje pacijenta.');
        }

        const json = await res.json();
        const patient = json.patient as Patient;

        setSelectedAppointmentPatient(patient);
        return patient;
    };

    const handleCreate = async (data: EventPayload) => {
        router.post('/events', {event: data as Record<string, any>});
        setIsCreateDialogOpen(false);
    };

    const handleUpdate = async (id: number, data: EventUpdatePayload) => {
        router.patch(`/events/${id}`, {event: data as Record<string, any>});
        setIsEditDialogOpen(false);
    };

    const handleDelete = async (event: Event): Promise<void> => {
        let keepHistory = true;
        let confirmMessage = 'Da li ste sigurni da želite da obrišete ovaj pregled?';

        if ('history_entry' in event && event.history_entry) {
            confirmMessage =
                'Brisanjem ovog pregleda biće obrisan i zapis u istoriji pacijenta. Ovu akciju nije moguće poništiti. Nastaviti?';
        }

        const confirmed = window.confirm(confirmMessage);
        if (!confirmed) return;

        if ('history_entry' in event && event.history_entry) {
            keepHistory = false;
        }

        await router.delete(`/events/${event.id}`, {
            data: {keep_history: keepHistory},
            onSuccess: () => {
                setIsEditDialogOpen(false);
                setEditingEvent(null);
            },
            onError: (errors: any) => {
                const msg = (errors && (errors.message || errors.error)) ?? 'Brisanje nije uspelo.';
                alert(msg);
            },
        });
    };

    const fetchCallList = async () => {
        const res = await fetch('/events/call_list');
        if (!res.ok) return;

        const json = await res.json();
        setCallListPatients(json.patients);
    };

    const toLocalInput = (d: Date) => {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
            d.getDate()
        )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const getDefaultTimes = (date: string | null) => {
        if (!date) return {start: '', end: ''};

        let startDate: Date;

        if (date.includes('T')) {
            startDate = new Date(date);
        } else {
            const now = new Date();
            const minutes = now.getMinutes();
            let roundedMinutes;
            if (minutes === 0) roundedMinutes = 0;
            else if (minutes <= 30) roundedMinutes = 30;
            else {
                roundedMinutes = 0;
                now.setHours(now.getHours() + 1);
            }

            const [y, m, d] = date.split('-').map(Number);
            startDate = new Date(y, m - 1, d, now.getHours(), roundedMinutes, 0, 0);
        }

        if (isNaN(startDate.getTime())) startDate = new Date();

        const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);

        return {start: toLocalInput(startDate), end: toLocalInput(endDate)};
    };
    const {start, end} = getDefaultTimes(selectedDate);

    const findNextEventForPatient = (patientId: string) => {
        if (!patientId) return;

        const now = Date.now();

        const patientEvents = (events || [])
            .filter((e: Event) => String(e.patient_id) === String(patientId))
            .filter((e: Event) => {
                const d = new Date(e.start_at);
                return !isNaN(d.getTime()) && d.getTime() > now;
            })
            .sort(
                (a: Event, b: Event) =>
                    new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
            );

        if (!patientEvents.length) {
            toast.info("Nema narednih termina za izabranog pacijenta.");
            return;
        }

        let index = nextEventIndex;

        if (lastPatientForNext !== patientId) {
            index = 0;
            setNextEventIndex(0);
            setLastPatientForNext(patientId);
        }

        const event = patientEvents[index];
        const date = new Date(event.start_at);

        if (isMobile) {
            calendarRef.current?.changeView('listDay', date);
        } else {
            calendarRef.current?.changeView('timeGridDay', date);
        }

        if (index + 1 < patientEvents.length) {
            setNextEventIndex(index + 1);
        } else {
            setNextEventIndex(0);
            toast.info("Poslednji termin");
        }
    };

    return (
        <AuthorizedLayout
            onSidebarToggle={() => {
                requestAnimationFrame(() => {
                    calendarRef.current?.updateSize();
                });
            }}
        >
            <div className='p-4 sm:p-6 space-y-6'>
                <header className='pb-4 border-b border-muted/20'>
                    <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3'>
                        <div className='flex flex-col'>
                            <PageTitle>{t.title}</PageTitle>
                        </div>

                        <div className='flex flex-wrap items-stretch sm:flex-row sm:items-center gap-3'>
                            <div className='flex items-center gap-2'>
                                <Label
                                    htmlFor='doctor-select'
                                    className='hidden sm:inline-block'
                                >
                                    {t?.filter_doctor || 'Filter by Doctor'}
                                </Label>

                                <div className='w-full sm:w-auto min-w-0'>
                                    <Select
                                        value={selectedDoctor || 'all'}
                                        onValueChange={(val: string) =>
                                            setSelectedDoctor(val === 'all' ? '' : val)
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder={t.all_doctors || 'Svi Doktori'}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value='all'>
                                                {t.all_doctors || 'Svi Doktori'}
                                            </SelectItem>
                                            {doctors.map((doc: Doctor) => (
                                                <SelectItem key={doc.id} value={doc.id}>
                                                    {doc.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 relative flex-grow min-w-0">
                                <Label
                                    htmlFor="patient-search"
                                    className="hidden sm:inline-block"
                                >
                                    {t?.filter_patient || "Pacijent"}
                                </Label>

                                <div className="relative w-full sm:w-64 min-w-0">
                                    <input
                                        id="patient-search"
                                        type="text"
                                        placeholder={t?.placeholder_patient_search || "Pretraga pacijenta"}
                                        value={patientSearch}
                                        onChange={(e) => {
                                            setPatientSearch(e.target.value);
                                            setSelectedPatient("");
                                            setShowSuggestions(true);
                                        }}
                                        onFocus={() => setShowSuggestions(true)}
                                        className="pl-3 pr-2 py-2 border rounded-md w-full text-sm"
                                        autoComplete="off"
                                    />

                                    {showSuggestions && patientSearch && (
                                        <ul className="absolute z-50 mt-1 max-h-40 w-full overflow-auto bg-white border rounded-md shadow-md">
                                            {patients
                                                .filter((p: { id: string; name: string }) =>
                                                    p.name.toLowerCase().includes(patientSearch.toLowerCase())
                                                )
                                                .slice(0, 10)
                                                .map((p: { id: string; name: string }) => (
                                                    <li
                                                        key={p.id}
                                                        className="px-2 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                                        onClick={() => {
                                                            setSelectedPatient(p.id);
                                                            setPatientSearch(p.name);
                                                            setShowSuggestions(false);
                                                        }}
                                                    >
                                                        {p.name}
                                                    </li>
                                                ))}
                                        </ul>
                                    )}
                                </div>
                                <Button
                                    size="sm"
                                    onClick={() => {
                                        if (!selectedPatient) {
                                            window.alert("Izaberite pacijenta.");
                                            return;
                                        }
                                        findNextEventForPatient(selectedPatient);
                                    }}
                                >
                                    <ArrowRight/>
                                </Button>
                            </div>

                            <div className='flex items-center gap-2'>
                                <Button
                                    size='icon'
                                    onClick={() => setIsCreateDialogOpen(true)}
                                    aria-label='Create'
                                >
                                    <Plus/>
                                </Button>
                            </div>
                        </div>
                    </div>
                </header>

                {isMobile ? (
                    <MobileAppointmentCalendar
                        events={filteredEvents}
                        onDateClick={openCreateDialog}
                        onEventClick={openEditDialog}
                        doctors={doctors}
                        calendarRefCallback={(cal) => (calendarRef.current = cal)}
                    />
                ) : (
                    <AppointmentCalendar
                        events={filteredEvents}
                        onDateClick={openCreateDialog}
                        onEventClick={openEditDialog}
                        onCallListClick={() => {
                            fetchCallList();
                            setIsCallListOpen(true);
                        }}
                        doctors={doctors}
                        calendarRefCallback={(cal) => (calendarRef.current = cal)}
                    />
                )}

                <CreateEventDialog
                    isOpen={isCreateDialogOpen}
                    onOpenChange={setIsCreateDialogOpen}
                    onCreate={handleCreate}
                    onCreatePatient={() => setIsCreatePatientDialogOpen(true)}
                    doctors={doctors}
                    start={start}
                    end={end}
                    doctor_id={selectedDoctor || undefined}
                    selectedPatient={selectedAppointmentPatient}
                    onPatientChange={setSelectedAppointmentPatient}
                />

                <CreatePatientDialog
                    isOpen={isCreatePatientDialogOpen}
                    onOpenChange={setIsCreatePatientDialogOpen}
                    onCreate={handleCreatePatient}
                    onCreated={(patient) => {
                        setSelectedAppointmentPatient(patient);
                        setIsCreateDialogOpen(true);
                    }}
                    doctors={doctors}
                    orthodontists={orthodontists}
                    defaultDoctorId={selectedDoctor || undefined}
                />

                {editingEvent && (
                    <EditEventDialog
                        isOpen={isEditDialogOpen}
                        onOpenChange={setIsEditDialogOpen}
                        onUpdate={(data) => handleUpdate(editingEvent.id, data)}
                        onDelete={handleDelete}
                        event={editingEvent}
                        doctors={doctors}
                    />
                )}

                {isCallListOpen && (
                    <CallListDialog
                        isOpen={isCallListOpen}
                        onOpenChange={setIsCallListOpen}
                        patients={callListPatients}
                    />
                )}
            </div>
        </AuthorizedLayout>
    );
}
